import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  getPageHeader,
  CallsListSkeleton,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderStack,
  PageLineTabs,
  useCallRecords,
} from '@yeshuman/sdk'
import type { PageLineTabItem } from '@yeshuman/sdk'
import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CallOutcomeRow } from './call-outcome-row'
import { CalendarCheck } from 'lucide-react'

import {
  useAppointmentResources,
  useAppointmentTypes,
  useCallOutcomes,
} from '../hooks/use-call-outcomes'
import {
  appointmentWindowQueryParams,
  parseAppointmentWindow,
  type AppointmentWindow,
} from '../lib/appointment-windows'

type CallOutcomesTab = 'outcomes' | 'reference'

const CALL_OUTCOMES_TAB_ITEMS: PageLineTabItem[] = [
  { value: 'outcomes', label: 'Outcomes' },
  { value: 'reference', label: 'Scheduling reference' },
]

const WINDOW_TAB_ITEMS: PageLineTabItem[] = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'today', label: 'Today' },
  { value: 'all', label: 'All' },
]

function emptyCopyForWindow(window: AppointmentWindow): string {
  if (window === 'today') {
    return 'No appointments booked for today yet.'
  }
  if (window === 'upcoming') {
    return 'No upcoming appointments in the next 7 days.'
  }
  return 'No booking outcomes yet. Booked calls will appear here.'
}

export function CallOutcomesPage() {
  const { title, description } = getPageHeader(
    '/calls/outcomes',
    'Outcomes',
    'Booked and upcoming appointments from call outcomes, plus scheduling reference data.'
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = (searchParams.get('tab') === 'reference' ? 'reference' : 'outcomes') as CallOutcomesTab
  const window = parseAppointmentWindow(searchParams.get('window'))

  const listParams = useMemo(() => appointmentWindowQueryParams(window), [window])
  const outcomesQuery = useCallOutcomes(listParams.limit, { params: listParams })
  const recordsQuery = useCallRecords(200)
  const appointmentTypesQuery = useAppointmentTypes()
  const resourcesQuery = useAppointmentResources()

  const outcomes = outcomesQuery.data ?? []
  const appointmentTypes = appointmentTypesQuery.data ?? []
  const resources = resourcesQuery.data ?? []

  const recordsById = useMemo(() => {
    const map = new Map((recordsQuery.data ?? []).map((r) => [r.id, r]))
    return map
  }, [recordsQuery.data])

  function setTab(value: CallOutcomesTab) {
    const next = new URLSearchParams(searchParams)
    if (value === 'outcomes') {
      next.delete('tab')
    } else {
      next.set('tab', value)
    }
    setSearchParams(next, { replace: true })
  }

  function setWindow(value: AppointmentWindow) {
    const next = new URLSearchParams(searchParams)
    if (value === 'upcoming') {
      next.delete('window')
    } else {
      next.set('window', value)
    }
    setSearchParams(next, { replace: true })
  }

  return (
    <PageContainer>
      <PageHeaderStack compact>
        <PageHeader
          leading={<PageHeaderLeading />}
          title={title}
          description={description}
          icon={<CalendarCheck className="h-5 w-5" />}
        />

        <PageLineTabs
          value={activeTab}
          onValueChange={(value) => setTab(value as CallOutcomesTab)}
          ariaLabel="Call outcomes sections"
          items={CALL_OUTCOMES_TAB_ITEMS}
        />
      </PageHeaderStack>

      {activeTab === 'outcomes' ? (
        <Card>
          <CardHeader className="space-y-4">
            <div>
              <CardTitle>Appointments</CardTitle>
              <CardDescription>
                Bookings linked to call outcomes. Open a row for the call transcript and
                booking workbench.
              </CardDescription>
            </div>
            <PageLineTabs
              value={window}
              onValueChange={(value) => setWindow(value as AppointmentWindow)}
              ariaLabel="Appointment time window"
              items={WINDOW_TAB_ITEMS}
            />
          </CardHeader>
          <CardContent className="space-y-2">
            {outcomesQuery.isLoading ? (
              <CallsListSkeleton count={4} ariaLabel="Loading appointments" />
            ) : outcomes.length === 0 ? (
              <p className="text-sm text-muted-foreground" role="status">
                {emptyCopyForWindow(window)}
              </p>
            ) : (
              outcomes.map((outcome) => (
                <CallOutcomeRow
                  key={outcome.id}
                  outcome={outcome}
                  row={recordsById.get(outcome.call_record_id) ?? null}
                />
              ))
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Scheduling reference</CardTitle>
            <CardDescription>
              Appointment types and resources exposed to booking tools (same data the
              assistant uses for lookups).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 @5col:grid-cols-2">
              <div className="space-y-3 text-sm">
                <p className="font-medium text-foreground">Appointment types</p>
                {appointmentTypesQuery.isLoading ? (
                  <CallsListSkeleton count={3} ariaLabel="Loading appointment types" />
                ) : (
                  appointmentTypes.map((item) => (
                    <div key={item.uid} className="rounded-md border p-4">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-muted-foreground text-xs mt-1">
                        UID: {item.uid} · Default {item.default_duration_minutes} min
                        {item.bookable === false ? ' · not bookable' : ''}
                      </p>
                    </div>
                  ))
                )}
              </div>
              <div className="space-y-3 text-sm">
                <p className="font-medium text-foreground">Resources</p>
                {resourcesQuery.isLoading ? (
                  <CallsListSkeleton count={3} ariaLabel="Loading resources" />
                ) : (
                  resources.map((item) => (
                    <div key={item.uid} className="rounded-md border p-4">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-muted-foreground text-xs mt-1">
                        UID: {item.uid}
                        {item.separation_name ? ` · ${item.separation_name}` : ''}
                        {item.bookable === false ? ' · not bookable' : ''}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  )
}
